import React, { useContext } from 'react';
import { forwardRef, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import MenuItem from '@material-ui/core/MenuItem';
import EventBusyIcon from '@material-ui/icons/EventBusy';
import {
  PromptContext,
  SnackbarContext,
  SnackbarVariant,
} from '@eas/common-web';
import { getUnitStartingPoint } from '@modules/planner/work-order-menu/menu-items/route';
import { updateWorkOrderTravelTime } from '@modules/planner/work-order-menu/menu-items/state';
import { unplanWorkOrder } from '@modules/work-order/work-order-api';
import { WorkOrder } from '@models';
import { useFetchEdgeWorkOrders } from './fetch-edge-work-orders';

type MenuItemProps = {
  closeMenu: () => void;
  workOrder: WorkOrder;
  afterSubmit: () => void;
  previousWorkOrder?: WorkOrder;
  nextWorkOrder?: WorkOrder;
};

function hasState(codes: string[], currentState: string | undefined) {
  return codes.includes(currentState || '');
}

export const UnplanWorkOrderMenuItem = forwardRef<HTMLLIElement, MenuItemProps>(
  function UnplanWorkOrderMenuItem({ closeMenu, workOrder, afterSubmit }, ref) {
    const { testPrompt } = useContext(PromptContext);
    const { showSnackbar } = useContext(SnackbarContext);

    const intl = useIntl();

    const { fetchEdgeWorkOrders } = useFetchEdgeWorkOrders(workOrder);

    const unplan = useCallback(async () => {
      try {
        const { previousWorkOrder, nextWorkOrder } =
          await fetchEdgeWorkOrders();
        const sp = getUnitStartingPoint(workOrder);
        await unplanWorkOrder(workOrder.id).json();
        await updateWorkOrderTravelTime(previousWorkOrder, nextWorkOrder, {
          latitude: sp?.latitude,
          longitude: sp?.longitude,
        });
      } catch (error) {
        showSnackbar(
          intl.formatMessage({
            id: 'WORK_ORDER_CHANGE_STATE_ERROR',
            defaultMessage: 'Nepodarilo sa zmeniť stav pracovného príkazu',
          }),
          SnackbarVariant.ERROR
        );
      }

      closeMenu();
      afterSubmit();
    }, [
      fetchEdgeWorkOrders,
      closeMenu,
      afterSubmit,
      workOrder,
      showSnackbar,
      intl,
    ]);

    if (!hasState(['NAP', 'ODL', 'ODM'], workOrder.currentState?.code)) {
      return null;
    }

    return (
      <MenuItem
        disabled={!workOrder.id || workOrder.locked}
        ref={ref}
        onClick={() => {
          if (workOrder.id) {
            testPrompt({
              key: `WORK_ORDER_UNPLAN_ALERT`,
              submitCallback: unplan,
            });
          }
        }}
      >
        <ListItemIcon>
          <EventBusyIcon fontSize="small" />
        </ListItemIcon>

        <FormattedMessage
          id="WORK_ORDER_MENU_ITEM_UNPLAN"
          defaultMessage="Odplánovať"
        />
      </MenuItem>
    );
  }
);
